import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDietaryIcon, getAllergenIcon } from '@/utils/iconMaps';

interface ChipOption {
  id: string;
  name: string;
  iconKey: string | null;
}

interface ChipSelectorProps {
  options: ChipOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  mode?: 'single' | 'multiple';
  colorScheme?: 'green' | 'amber';
  type?: 'dietary' | 'allergen';
}

export const ChipSelector: React.FC<ChipSelectorProps> = ({
  options,
  selected,
  onChange,
  mode = 'multiple',
  colorScheme = 'green',
  type = 'dietary',
}) => {
  const handleToggle = (id: string) => {
    if (mode === 'single') {
      onChange([id]);
    } else {
      if (selected.includes(id)) {
        onChange(selected.filter((item) => item !== id));
      } else {
        onChange([...selected, id]);
      }
    }
  };

  const getIcon = (iconKey: string | null) => {
    if (type === 'dietary') {
      return getDietaryIcon(iconKey);
    }
    return getAllergenIcon(iconKey);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {options.map((option) => {
        const isSelected = selected.includes(option.id);

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => handleToggle(option.id)}
            className={cn(
              'relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 min-h-[88px]',
              'hover:scale-105 active:scale-95',
              // Inactive state
              !isSelected && 'bg-muted/50 border-border text-muted-foreground hover:border-border/80',
              // Active state - Green (Dietary)
              isSelected && colorScheme === 'green' && 'bg-[#cfef00]/20 border-[#cfef00] text-foreground ring-1 ring-[#cfef00]',
              // Active state - Amber (Allergen)
              isSelected && colorScheme === 'amber' && 'bg-amber-500/15 border-amber-500 text-amber-700 dark:text-amber-400 ring-1 ring-amber-500'
            )}
          >
            {/* Checkmark overlay */}
            {isSelected && (
              <div className="absolute top-2 right-2">
                <Check className={cn(
                  'h-4 w-4',
                  colorScheme === 'green' && 'text-foreground',
                  colorScheme === 'amber' && 'text-amber-700 dark:text-amber-400'
                )} />
              </div>
            )}

            {/* Icon */}
            <div className={cn(
              'transition-opacity',
              isSelected ? 'opacity-100' : 'opacity-60'
            )}>
              {getIcon(option.iconKey)}
            </div>

            {/* Label */}
            <span className={cn(
              'text-xs font-medium text-center leading-tight',
              isSelected && 'font-semibold'
            )}>
              {option.name}
            </span>
          </button>
        );
      })}
    </div>
  );
};
